import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Route } from "lucide-react";
import notification from "../../../../helpers/notification";
import { TspBreadcrumb, TspEmptyState } from "../../components/primitives";
import TspLoadError from "../../components/common/TspLoadError";
import TspApiService from "../../api/tspApiService";
import { tspErrorMessage } from "../../hooks/useTspApiResource";
import useTspPermission from "../../hooks/useTspPermission";
import TSP_PERMISSIONS from "../../constants/tspPermissions";
import useTspRoutePlanningData from "./useTspRoutePlanningData";
import { addStop, isRouteDirty, moveStop, removeStop, routeToStops, stopsToPayload, toHistoryItem } from "./routePlanningModel";
import RouteSequenceCard from "./components/RouteSequenceCard";
import RouteHistoryCard from "./components/RouteHistoryCard";
import TspBackButton from "../../components/common/TspBackButton";

const ROUTE_EDITABLE = ["DRAFT", "PLANNED", "APPROVED", "IN_PROGRESS"];

// Route Planning. The route is read from, and saved to, the server; edits are
// held here until Save Route, after which the route and its history are re-read
// so the screen shows what was actually stored.
export default function TspRoutePlanningPage() {
  const { search } = useLocation();
  const { hasPermission } = useTspPermission();
  const canManage = hasPermission(TSP_PERMISSIONS.ROUTE_PLANNING_MANAGE);
  const { journeyId, setJourneyId, journeys, route, history, states, reloadRoute } = useTspRoutePlanningData({
    initialJourneyId: new URLSearchParams(search).get("journey"),
    canManage,
  });

  const [stops, setStops] = useState([]);
  const [saving, setSaving] = useState(false);
  const stopCounter = useRef(0);

  // Whenever the saved route is (re)loaded, the editable list starts from it.
  useEffect(() => {
    setStops(routeToStops(route.data));
  }, [route.data]);

  if (journeys.error) return <TspLoadError error={journeys.error} onRetry={journeys.reload} what="journeys" />;
  if (!journeys.data) return <div className="tsp-page tsp-dashboard-loading">Loading route planning…</div>;

  const savedRoute = route.data;
  const status = savedRoute?.journey.status;
  const canEdit = canManage && Boolean(savedRoute) && ROUTE_EDITABLE.includes(status);
  const lockedReason = savedRoute && !ROUTE_EDITABLE.includes(status) ? `This journey is ${status.replace("_", " ").toLowerCase()}, so its route can no longer be changed.` : null;
  const dirty = Boolean(savedRoute) && isRouteDirty(stops, savedRoute);

  const options = [...journeys.data.map((journey) => ({ id: String(journey.id), name: journey.journey_name }))];
  if (savedRoute && !options.some((option) => option.id === String(savedRoute.journey.id))) {
    options.unshift({ id: String(savedRoute.journey.id), name: savedRoute.journey.journey_name });
  }

  const handleAdd = (place) => {
    const result = addStop(stops, place, () => `new-${(stopCounter.current += 1)}`);
    if (result.ok) setStops(result.stops);
    return result;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await TspApiService.saveRoute(journeyId, stopsToPayload(stops));
      notification("success", "Route saved.");
      reloadRoute();
    } catch (error) {
      // Keep the local edits so nothing is lost; the reason is on screen.
      notification("error", tspErrorMessage(error, "The route could not be saved."));
      if (error?.response?.status === 409) reloadRoute();
    } finally {
      setSaving(false);
    }
  };

  const renderRoute = () => {
    if (route.error) return <TspLoadError error={route.error} onRetry={route.reload} what="this route" />;
    if (!savedRoute) return <div className="tsp-page tsp-dashboard-loading">Loading route…</div>;
    return (
      <RouteSequenceCard
        stops={stops}
        states={(states.data || []).map((state) => ({ id: String(state.id), name: state.name }))}
        canEdit={canEdit}
        lockedReason={lockedReason}
        dirty={dirty}
        saving={saving}
        onMove={(index, direction) => setStops((current) => moveStop(current, index, direction))}
        onRemove={(key) => setStops((current) => removeStop(current, key))}
        onAdd={handleAdd}
        onSave={handleSave}
      />
    );
  };

  return (
    <div className="tsp-page tsp-route-planning-page">
      <div className="tsp-page-heading-row">
        <div>
          <h1>Route Planning</h1>
          <TspBreadcrumb items={["Dashboard", "Route Planning"]} />
        </div>
        <TspBackButton fallback="/tsp" />
      </div>

      {options.length === 0 ? (
        <TspEmptyState
          icon={Route}
          title="No journeys to plan"
          message="Create a journey first, then plan its route here."
          action={
            <Link to="/tsp/journeys/new" className="tsp-btn tsp-btn-success">
              Create Journey
            </Link>
          }
        />
      ) : (
        <>
          <div className="tsp-card">
            <div className="tsp-form-field" style={{ maxWidth: 360 }}>
              <label htmlFor="rp-journey">Select Journey</label>
              <select
                id="rp-journey"
                className="tsp-select tsp-select-block"
                value={journeyId}
                disabled={saving}
                onChange={(event) => {
                  if (dirty && !window.confirm("You have unsaved route changes. Switch journey and discard them?")) return;
                  setJourneyId(event.target.value);
                }}
              >
                {options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="tsp-grid tsp-grid-2-1">
            {renderRoute()}
            {history.error ? (
              <TspLoadError error={history.error} onRetry={history.reload} what="the route history" />
            ) : (
              <RouteHistoryCard history={(history.data || []).map(toHistoryItem)} />
            )}
          </div>
        </>
      )}

      <ToastContainer />
    </div>
  );
}
